'use client';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { FavoriteService } from '@/hotdesk-ui/services/favorite.service';
import { RootState } from '../store';

interface FavoritesState {
    /** Map of listingId → favoriteId for O(1) lookups */
    items: Record<number, number>;
    loading: boolean;
    error: string | null;
}

const initialState: FavoritesState = {
    items: {},
    loading: false,
    error: null
};

/** Fetch all favorites for the current user */
export const fetchFavorites = createAsyncThunk(
    'favorites/fetchFavorites',
    async (userId: number, { getState }) => {
        const state = getState() as RootState;
        const { defaultLanguage } = state.listing;
        const apiConfig = await new ApiConfigration().getApiConfig(defaultLanguage);
        const service = new FavoriteService(apiConfig);
        const response = await service.getUserFavorites(userId, 500);
        const results = (response as any).results || [];

        // Build a listingId → favoriteId map.
        // The list endpoint returns `listing` as an expanded object, while
        // the create endpoint accepts a primitive id — handle both shapes.
        const items: Record<number, number> = {};
        for (const fav of results) {
            const listingId =
                typeof fav.listing === 'object' && fav.listing !== null
                    ? (fav.listing as { id?: number }).id
                    : fav.listing;
            if (typeof listingId === 'number' && fav.id) {
                items[listingId] = fav.id;
            }
        }
        return items;
    }
);

/** Add a favorite */
export const addFavorite = createAsyncThunk(
    'favorites/addFavorite',
    async (
        { userId, listingId }: { userId: number; listingId: number },
        { getState }
    ) => {
        const state = getState() as RootState;
        const { defaultLanguage } = state.listing;
        const apiConfig = await new ApiConfigration().getApiConfig(defaultLanguage);
        const service = new FavoriteService(apiConfig);
        const result = await service.addFavorite(userId, listingId);
        return { listingId, favoriteId: result.id! };
    }
);

/** Remove a favorite */
export const removeFavorite = createAsyncThunk(
    'favorites/removeFavorite',
    async (
        { listingId, favoriteId }: { listingId: number; favoriteId: number },
        { getState }
    ) => {
        const state = getState() as RootState;
        const { defaultLanguage } = state.listing;
        const apiConfig = await new ApiConfigration().getApiConfig(defaultLanguage);
        const service = new FavoriteService(apiConfig);
        await service.removeFavorite(favoriteId);
        return { listingId };
    }
);

const favoritesSlice = createSlice({
    name: 'favorites',
    initialState,
    reducers: {
        clearFavorites: (state) => {
            state.items = {};
            state.loading = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchFavorites.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFavorites.fulfilled, (state, action: PayloadAction<Record<number, number>>) => {
                state.items = action.payload;
                state.loading = false;
            })
            .addCase(fetchFavorites.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch favorites';
            })
            // Add
            .addCase(addFavorite.fulfilled, (state, action) => {
                state.items[action.payload.listingId] = action.payload.favoriteId;
            })
            // Remove
            .addCase(removeFavorite.fulfilled, (state, action) => {
                delete state.items[action.payload.listingId];
            });
    }
});

export const { clearFavorites } = favoritesSlice.actions;
export default favoritesSlice.reducer;
